import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { isTouch } from "@/lib/touch";
import { cn } from "@/lib/utils";

/**
 * v2.10 P5.1 (D-138): the one note box — adding and editing, on a lead, a card and a call-log entry.
 *
 * An auto-sizing textarea (1 line growing to 8, then it scrolls). On a desktop Enter submits, Shift+Enter
 * is a new line, ⌘/Ctrl+Enter submits too, Esc cancels an edit. On a touch screen Enter is a new line and
 * the Send button submits. A multi-line paste is kept as typed; the notes render with `whitespace-pre-wrap`.
 */
const MAX_LINES = 8;

export function NoteComposer({
  value,
  onChange,
  onSubmit,
  onCancel,
  placeholder = "Add a note…",
  submitLabel = "Add",
  testId = "note",
  disabled = false,
  autoFocus = false,
  busy = false,
  className,
  inputRef,
  ids,
  canSubmit = true,
}: {
  value: string;
  onChange: (next: string) => void;
  onSubmit: (body: string) => void | Promise<unknown>;
  /** Editing: Esc and the Cancel button call this. */
  onCancel?: () => void;
  placeholder?: string;
  submitLabel?: string;
  testId?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  busy?: boolean;
  className?: string;
  inputRef?: React.MutableRefObject<HTMLTextAreaElement | null>;
  /** Test ids kept from the composers this one replaced (`note-editor`, `-save`, `-cancel`). */
  ids?: { body?: string; submit?: string; cancel?: string };
  /** False keeps typing open but the submit off (the call-log note before a chip is picked). */
  canSubmit?: boolean;
}) {
  const area = useRef<HTMLTextAreaElement | null>(null);
  const [touch, setTouch] = useState(false);
  useEffect(() => setTouch(isTouch()), []);

  useLayoutEffect(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = "auto";
    const line = parseFloat(getComputedStyle(el).lineHeight) || 20;
    const pad = el.offsetHeight - el.clientHeight + 8;
    const max = line * MAX_LINES + pad;
    el.style.height = `${Math.min(el.scrollHeight + 2, max)}px`;
    el.style.overflowY = el.scrollHeight + 2 > max ? "auto" : "hidden";
  }, [value]);

  useEffect(() => {
    if (autoFocus) {
      area.current?.focus();
      const end = area.current?.value.length ?? 0;
      area.current?.setSelectionRange(end, end);
    }
  }, [autoFocus]);

  const submit = () => {
    if (busy || disabled || !canSubmit || !value.trim()) return;
    void onSubmit(value.replace(/\s+$/, ""));
  };

  return (
    <div className={cn("w-full", className)} data-testid={`${testId}-composer`}>
      <div className="flex items-end gap-2">
        <textarea
          ref={(el) => {
            area.current = el;
            if (inputRef) inputRef.current = el;
          }}
          rows={1}
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          aria-label={placeholder}
          data-testid={ids?.body ?? `${testId}-body`}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.nativeEvent.isComposing) return;
            if (e.key === "Escape" && onCancel) {
              e.preventDefault();
              e.stopPropagation();
              onCancel();
              return;
            }
            if (e.key !== "Enter") return;
            if (e.metaKey || e.ctrlKey) {
              e.preventDefault();
              submit();
              return;
            }
            if (e.shiftKey || touch) return; // a new line
            e.preventDefault();
            submit();
          }}
          className={cn(
            "min-h-9 w-full resize-none rounded-md border bg-surface px-2.5 py-1.5 text-sm leading-5",
            "focus:outline-none focus:ring-2 focus:ring-accent/40 pointer-coarse:text-base",
          )}
        />
        <button
          type="button"
          onClick={submit}
          disabled={busy || disabled || !canSubmit || !value.trim()}
          data-testid={ids?.submit ?? `${testId}-submit`}
          className="inline-flex min-h-9 shrink-0 items-center rounded-md border border-brand bg-brand px-3 text-sm font-medium text-brand-fg disabled:opacity-50 pointer-coarse:min-h-11"
        >
          {submitLabel}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            data-testid={ids?.cancel ?? `${testId}-cancel`}
            className="inline-flex min-h-9 shrink-0 items-center rounded-md border px-3 text-sm pointer-coarse:min-h-11"
          >
            Cancel
          </button>
        )}
      </div>
      <p className="mt-0.5 text-[10px] text-fg-muted" data-testid={`${testId}-hint`}>
        {touch
          ? `Return adds a line · tap ${submitLabel}`
          : `Enter ${submitLabel.toLowerCase()}s · Shift+Enter new line · ⌘/Ctrl+Enter ${submitLabel.toLowerCase()}s${onCancel ? " · Esc cancels" : ""}`}
      </p>
    </div>
  );
}
